import { useState } from 'react';
import LearningProgressChart from './LearningProgressChart';

const HowTo = () => {
    const [showCaseStudy, setShowCaseStudy] = useState(false);
    const [showProgressStats, setShowProgressStats] = useState(false);
    const [showSpacedRepetitionCurve, setShowSpacedRepetitionCurve] = useState(false);

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 lg:p-8 space-y-8">
            {/* Header */}
            <div className="text-center mb-8">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 font-mono mb-2">
                    How to Use The Catchphraser
                </h1>
                <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
            </div>

            {/* 1. Practice Methods (Shadowing & Echoing) */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-blue-500 pl-4">
                    Practice Methods
                </h2>

                <div className="grid md:grid-cols-2 gap-6">
                    {/* Shadowing */}
                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border-2 border-blue-200">
                        <div className="flex items-center mb-4">
                            <div className="text-2xl mr-3">🎧</div>
                            <h3 className="text-lg font-semibold text-gray-800">Shadowing</h3>
                        </div>
                        <p className="text-gray-600 leading-relaxed mb-3">
                            Speak simultaneously with the narrator, matching their rhythm and intonation in real time.
                        </p>
                        <div className="space-y-2 text-sm text-gray-700">
                            <div className="flex items-start">
                                <div className="w-4 h-4 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-2 mt-0.5">•</div>
                                <span>Focus on speech flow and timing</span>
                            </div>
                            <div className="flex items-start">
                                <div className="w-4 h-4 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-2 mt-0.5">•</div>
                                <span>Use headphones to avoid recording the narrator audio</span>
                            </div>
                            <div className="flex items-start">
                                <div className="w-4 h-4 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-2 mt-0.5">•</div>
                                <span>Aim for rhythm accuracy first, then word accuracy</span>
                            </div>
                        </div>
                    </div>

                    {/* Echoing */}
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200">
                        <div className="flex items-center mb-4">
                            <div className="text-2xl mr-3">🎯</div>
                            <h3 className="text-lg font-semibold text-gray-800">Echoing</h3>
                        </div>
                        <p className="text-gray-600 leading-relaxed mb-3">
                            Listen, pause, then reproduce specific sounds or phrases with precision.
                        </p>
                        <div className="space-y-2 text-sm text-gray-700">
                            <div className="flex items-start">
                                <div className="w-4 h-4 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold mr-2 mt-0.5">•</div>
                                <span>Focus on individual sounds and stress placement</span>
                            </div>
                            <div className="flex items-start">
                                <div className="w-4 h-4 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold mr-2 mt-0.5">•</div>
                                <span>Use playback controls to repeat challenging sections</span>
                            </div>
                            <div className="flex items-start">
                                <div className="w-4 h-4 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold mr-2 mt-0.5">•</div>
                                <span>Compare your articulation to the model recording</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border">
                    <img 
                        alt="Audio interface demonstration" 
                        className="w-full rounded-lg shadow-sm"
                        src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/response_ai.png`} 
                    />
                </div>
            </section>

            {/* 2. Cognitive Advantage */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-green-500 pl-4">
                    Why These Methods Work
                </h2>
                
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border-2 border-green-200">
                    <div className="flex items-center mb-4">
                        <div className="text-2xl mr-3">🧠</div>
                        <h3 className="text-lg font-semibold text-gray-800">Cognitive Advantage</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <p className="text-gray-700 mb-3">
                                By practicing pronunciation <span className="font-semibold">separately from meaning</span>, your brain can focus entirely on sound production.
                            </p>
                            <div className="space-y-2">
                                <div className="flex items-start">
                                    <div className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm mr-3 mt-0.5">•</div>
                                    <span className="text-sm text-gray-700">Reduces cognitive load on vocabulary and grammar</span>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm mr-3 mt-0.5">•</div>
                                    <span className="text-sm text-gray-700">Develops automaticity in speech muscle coordination</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="bg-white p-4 rounded-lg border border-green-200">
                                <h4 className="font-semibold text-gray-800 mb-2">💡 Optimal Practice</h4>
                                <p className="text-sm text-gray-700 mb-2">
                                    Practice 15-20 minutes daily for consistent improvement.
                                </p>
                                <p className="text-xs text-gray-600">
                                    Focus on sound and rhythm, not word meaning, during pronunciation exercises.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

           

            {/* 4. Pronunciation Assessment (Sound Accuracy) */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-blue-500 pl-4">
                    Pronunciation Assessment (Sound Accuracy)
                </h2>
                
                <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
                    {/* Assessment Types Clarification */}
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <p className="text-sm text-gray-700">
                            Catchphraser provides <span className="font-semibold">two complementary types of feedback</span>:
                        </p>
                        <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc list-inside">
                            <li>
                                <strong>Pronunciation Assessment</strong> — how accurately you pronounce sounds and words
                            </li>
                            <li>
                                <strong>Prosody Analysis</strong> — how natural your pitch, stress, and rhythm are
                            </li>
                        </ul>
                    </div>

                    {/* Core Metrics */}
                    <div>
                        <h3 className="font-semibold text-gray-800 mb-1">
                            Pronunciation Metrics (Sound-Level)
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            These scores measure how accurately you pronounce the reference text.
                        </p>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="bg-white p-4 rounded-lg border">
                                <h4 className="font-semibold text-gray-800 mb-2">🎯 Accuracy</h4>
                                <p className="text-sm text-gray-600">How closely phonemes match native pronunciation</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg border">
                                <h4 className="font-semibold text-gray-800 mb-2">💬 Fluency</h4>
                                <p className="text-sm text-gray-600">How well speech matches native pause patterns</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg border">
                                <h4 className="font-semibold text-gray-800 mb-2">📊 Completeness</h4>
                                <p className="text-sm text-gray-600">Ratio of pronounced words to reference text</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg border">
                                <h4 className="font-semibold text-gray-800 mb-2">⭐ Overall Score</h4>
                                <p className="text-sm text-gray-600">Combined score based on all metrics</p>
                            </div>
                        </div>
                    </div>

                    {/* Color Coding System */}
                    <div className="pt-6 border-t border-gray-200">
                        <h3 className="font-semibold text-gray-800 mb-4">Color Coding System</h3>
                        <div className="bg-white p-5 rounded-lg border-2 border-gray-100">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-8 mb-4">
                                <div className="flex items-center">
                                    <div className="w-5 h-5 bg-green-500 rounded-full mr-3"></div>
                                    <div>
                                        <div className="font-medium text-gray-800">80-100%</div>
                                        <div className="text-xs text-gray-600">Excellent</div>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <div className="w-5 h-5 bg-yellow-500 rounded-full mr-3"></div>
                                    <div>
                                        <div className="font-medium text-gray-800">40-79%</div>
                                        <div className="text-xs text-gray-600">Good</div>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <div className="w-5 h-5 bg-red-500 rounded-full mr-3"></div>
                                    <div>
                                        <div className="font-medium text-gray-800">0-39%</div>
                                        <div className="text-xs text-gray-600">Needs Practice</div>
                                    </div>
                                </div>
                            </div>
                            <p className="text-sm text-gray-600">
                                Applied to individual words and <a className="text-blue-600 hover:text-blue-800 font-medium" target="_blank" rel="noopener noreferrer" href="https://www.britannica.com/topic/phoneme">phonemes</a> (shown as dots below words)
                            </p>
                        </div>
                    </div>

                    {/* Phoneme Level Assessment */}
                    <div className="pt-6 border-t border-gray-200">
                        <h3 className="font-semibold text-gray-800 mb-4">Phoneme-Level Assessment</h3>
                        <div className="bg-blue-50 rounded-xl p-5 border-2 border-blue-200">
                            <div className="flex items-start mb-3">
                                <div className="text-lg mr-3">🔤</div>
                                <div>
                                    <h4 className="font-semibold text-gray-800 mb-1">Individual Sound Analysis</h4>
                                    <p className="text-sm text-gray-600 mb-3">
                                        Each phoneme (distinct sound unit) receives its own accuracy score, allowing precise identification of specific sound difficulties.
                                    </p>
                                </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="bg-white p-4 rounded-lg border">
                                    <h5 className="font-medium text-gray-800 mb-1">Targeted Practice</h5>
                                    <p className="text-xs text-gray-600">Identify exactly which sounds need improvement</p>
                                </div>
                                <div className="bg-white p-4 rounded-lg border">
                                    <h5 className="font-medium text-gray-800 mb-1">Progress Tracking</h5>
                                    <p className="text-xs text-gray-600">Monitor improvement on specific phonemes over time</p>
                                </div>
                            </div>
                            <p className="text-xs text-gray-600 mt-3">
                                Unlike prosody analysis, phoneme scores directly affect your overall pronunciation score.
                            </p>
                        </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 border">
                        <p className="text-sm text-gray-600">
                            Powered by <a className="text-blue-600 hover:text-blue-800 font-semibold" target="_blank" rel="noopener noreferrer" href="https://learn.microsoft.com/en-us/azure/ai-services/speech-service/how-to-pronunciation-assessment?pivots=programming-language-python">Microsoft Pronunciation Assessment API</a>
                        </p>
                    </div>
                </div>
            </section>

            {/* 5. Prosody Analysis (Visual Feedback Only) */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-yellow-500 pl-4">
                    Prosody Analysis (Pitch, Stress & Rhythm)
                </h2>

                 <div className="bg-gray-50 rounded-xl p-4 border">
                    <img 
                        alt="Audio interface demonstration" 
                        className="w-full rounded-lg shadow-sm"
                        src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/response_pitch.png`} 
                    />
                </div>
                
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <p className="text-sm text-gray-600 mb-6">
                        This section provides <span className="font-semibold">visual feedback only</span> and
                        does <span className="font-semibold">not affect your pronunciation score</span>.
                    </p>

                    <div className="grid md:grid-cols-3 gap-6 mb-6">
                        <div className="bg-yellow-50 rounded-xl p-5 border-2 border-yellow-200">
                            <div className="flex items-center mb-3">
                                <div className="text-xl mr-3">🎵</div>
                                <h4 className="font-semibold text-gray-800">Pitch Contour</h4>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                                Visualizes how your voice rises and falls throughout sentences
                            </p>
                            <p className="text-xs text-gray-700 font-medium">
                                Helps match natural intonation patterns
                            </p>
                        </div>
                        
                        <div className="bg-yellow-50 rounded-xl p-5 border-2 border-yellow-200">
                            <div className="flex items-center mb-3">
                                <div className="text-xl mr-3">🔊</div>
                                <h4 className="font-semibold text-gray-800">Intensity</h4>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                                Highlights stressed syllables and volume patterns
                            </p>
                            <p className="text-xs text-gray-700 font-medium">
                                Improves word stress and emphasis
                            </p>
                        </div>
                        
                        <div className="bg-yellow-50 rounded-xl p-5 border-2 border-yellow-200">
                            <div className="flex items-center mb-3">
                                <div className="text-xl mr-3">🎼</div>
                                <h4 className="font-semibold text-gray-800">Rhythm</h4>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                                Shows overall speech melody and timing patterns
                            </p>
                            <p className="text-xs text-gray-700 font-medium">
                                Develops natural speech flow
                            </p>
                        </div>
                    </div>
                    
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-gray-700">
                            <span className="font-semibold">Cognitive benefit:</span> Visual feedback helps retrain automatic speech patterns by making subtle prosodic features consciously observable.
                        </p>
                    </div>
                </div>
            </section>

        

             {/* 3. Spaced Repetition & Muscle Memory (Mobile-Friendly) */}
<section className="space-y-6">
  <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-indigo-500 pl-4">
    How Learning Actually Happens (Our Practice Strategy)
  </h2>

  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-200 space-y-6">
    
    {/* Intro */}
    <div className="flex items-start">
      <div className="text-2xl mr-3">🔄</div>
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-1">
          Spaced Repetition & Muscle Memory
        </h3>
        <p className="text-sm text-gray-600">
          Pronunciation is a physical skill. Like any motor ability, it improves through
          repeated, well-timed practice over time.
        </p>
      </div>
    </div>

    {/* Always-visible key insight */}
    <div className="bg-white p-4 rounded-lg border border-indigo-100">
      <h4 className="font-semibold text-gray-800 mb-2">📉 Expected Regression</h4>
      <p className="text-sm text-gray-600">
        If a phoneme you once mastered turns{" "}
        <span className="font-semibold text-yellow-600">orange</span> again, that's normal.
        Without reinforcement, both memory and speech coordination naturally weaken.
      </p>
    </div>

    {/* Desktop: full content | Mobile: collapsible */}
    <details className="md:hidden bg-white rounded-lg border border-indigo-100">
      <summary className="cursor-pointer px-4 py-3 font-medium text-indigo-700">
        Learn how reinforcement works
      </summary>

      <div className="px-4 pb-4 space-y-4 text-sm text-gray-600">
        <div>
          <h5 className="font-semibold text-gray-800 mb-1">📈 Reinforcement Curve</h5>
          <p>
            Each successful retrieval strengthens neural pathways.
            Forgetting slows with each well-timed review, making future recall easier.
          </p>
        </div>

        <div>
          <h5 className="font-semibold text-gray-800 mb-1">🧠 Learning Cycle</h5>
          <ol className="list-decimal list-inside space-y-1">
            <li>Initial learning → rapid forgetting</li>
            <li>First reinforcement → slower decay</li>
            <li>Repeated retrieval → stabilization</li>
            <li>Long-term retention → automaticity</li>
          </ol>
        </div>

        <div>
          <h5 className="font-semibold text-gray-800 mb-1">💪 Muscle Memory</h5>
          <p>
            Pronunciation relies on procedural (motor) memory.
            Short, regular sessions outperform occasional intensive drills.
          </p>
        </div>
      </div>
    </details>

    {/* Desktop-only expanded view */}
    <div className="hidden md:grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="bg-white p-4 rounded-lg border border-indigo-100">
          <h4 className="font-semibold text-gray-800 mb-2">📈 The Reinforcement Curve</h4>
          <p className="text-sm text-gray-600">
            Each successful retrieval strengthens neural pathways.
            As sounds are revisited over spaced intervals, retention stabilizes
            and effort decreases.
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg border border-indigo-100">
          <h4 className="font-semibold text-gray-800 mb-2">💪 Muscle Memory Development</h4>
          <p className="text-sm text-gray-600">
            Pronunciation learning depends on procedural memory.
            Spaced repetition determines <span className="font-semibold">when</span>  practice happens for maximum effect.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-5 border-2 border-indigo-200">
        <h4 className="font-semibold text-gray-800 mb-3">
          Spaced Repetition Learning Cycle
        </h4>

        <ol className="space-y-3 text-sm text-gray-600">
          <li><strong>1.</strong> Initial learning → fast forgetting</li>
          <li><strong>2.</strong> First reinforcement → slower decay</li>
          <li><strong>3.</strong> Subsequent reviews → stability</li>
          <li><strong>4.</strong> Long-term retention → automatic speech</li>
        </ol>
      </div>
    </div>

    {/* Optional curve visualization */}
    {showSpacedRepetitionCurve && (
      <div className="pt-6 border-t border-indigo-200">
        <LearningProgressChart />
      </div>
    )}

    <div className="flex justify-center pt-2">
      <button
        onClick={() => setShowSpacedRepetitionCurve(!showSpacedRepetitionCurve)}
        className="text-sm px-4 py-2 border border-indigo-300 rounded-lg text-indigo-700 hover:bg-indigo-50 transition"
      >
        {showSpacedRepetitionCurve ? 'Hide Learning Curve' : 'Show Learning Curve'}
      </button>
    </div>

  </div>
</section>


            {/* 7. Student Case Study (Expandable) */}
            <section className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-purple-500 pl-4">
                        Real-World Results
                    </h2>
                    <button
                        onClick={() => setShowCaseStudy(!showCaseStudy)}
                        className="text-sm text-purple-600 hover:text-purple-800 font-medium px-3 py-1 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
                    >
                        {showCaseStudy ? 'Hide Details' : 'Show Details'}
                    </button>
                </div>
                
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <p className="text-gray-600 mb-4">
                        Pavel (10) practed English with "Harry Potter". A sample excerpt which he practiced:
                    </p>
                    
                    <audio 
                        preload="auto" 
                        controls 
                        className="w-full max-w-md mx-auto mb-4"
                        src="https://www.catchphraser.com/api/uploads/temp/671977801ece29e04da62847/English/Audiobook/J.%20K.%20Rowling/Harry%20Potter%20and%20the%20Chamber%20of%20Secrets/0.34_9.0_Harry%20Potter%20and%20the%20Chamber%20of%20Secrets%20-%20CD1%20-%2006%20-%20Aunt%20Petunia's%20pudding.wav" 
                        type="wav/audio"
                    />

                    {showCaseStudy && (
                        <div className="space-y-6 mt-6 pt-6 border-t border-gray-200">
                            <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                                <h4 className="font-semibold text-gray-800 mb-3">First Attempt</h4>
                                <audio 
                                    preload="auto" 
                                    controls 
                                    className="w-full max-w-md mx-auto mb-4"
                                    src="https://www.catchphraser.com/api/uploads/feedback/673e1f4b28b69c1a27f110e8/English/Audiobook/J.%20K.%20Rowling/Harry%20Potter%20and%20the%20Chamber%20of%20Secrets/0.34_9.0_1742758705_Harry%20Potter%20and%20the%20Chamber%20of%20Secrets%20-%20CD1%20-%2006%20-%20Aunt%20Petunia's%20pudding.wav" 
                                    type="wav/audio"
                                />
                                <img 
                                    alt="Pavel's first attempt assessment" 
                                    className="w-full rounded-lg shadow-sm"
                                    src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/pasha_screen_00.png`} 
                                />
                            </div>

                            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                                <h4 className="font-semibold text-gray-800 mb-3">After Two Weeks</h4>
                                <audio 
                                    preload="auto" 
                                    controls 
                                    className="w-full max-w-md mx-auto mb-4"
                                    src="https://www.catchphraser.com/api/uploads/feedback/673e1f4b28b69c1a27f110e8/English/Audiobook/J.%20K.%20Rowling/Harry%20Potter%20and%20the%20Chamber%20of%20Secrets/0.34_9.0_1744224051_Harry%20Potter%20and%20the%20Chamber%20of%20Secrets%20-%20CD1%20-%2006%20-%20Aunt%20Petunia's%20pudding.wav" 
                                    type="wav/audio"
                                />
                                <img 
                                    alt="Pavel's improved assessment" 
                                    className="w-full rounded-lg shadow-sm"
                                    src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/pasha_screen_01.png`} 
                                />
                            </div>
                            
                            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                                <p className="text-gray-700 font-semibold">
                                    ✅ Overall scores for Accuracy, Fluency, Completeness, and Pronunciation showed significant improvement after systematic practice.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* 8. Progress Tracking (Expandable) */}
            <section className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-purple-500 pl-4">
                        Track Your Improvement
                    </h2>
                    <button
                        onClick={() => setShowProgressStats(!showProgressStats)}
                        className="text-sm text-purple-600 hover:text-purple-800 font-medium px-3 py-1 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
                    >
                        {showProgressStats ? 'Hide Details' : 'Show Details'}
                    </button>
                </div>
                
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <p className="text-gray-600 mb-4">
                        Monitor your long-term progress with detailed statistics accessible from your profile.
                    </p>
                    
                    {showProgressStats && (
                        <div className="space-y-6 mt-4 pt-4 border-t border-gray-200">
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <h4 className="font-semibold text-gray-800 mb-3">Sentence Accuracy Trends</h4>
                                <img 
                                    alt="Sentence accuracy statistics" 
                                    className="w-full rounded-lg shadow-sm"
                                    src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/stats_sentences.png`} 
                                />
                            </div>
                            
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <h4 className="font-semibold text-gray-800 mb-3">Phoneme Performance</h4>
                                <img 
                                    alt="Phoneme accuracy statistics" 
                                    className="w-full rounded-lg shadow-sm"
                                    src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/stats_phonemes.png`} 
                                />
                            </div>

                            <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                                <p className="text-purple-800 font-semibold">🚀 Future Development</p>
                                <p className="text-purple-700 text-sm mt-1">
                                    Upcoming: Personalized minimal pair exercises powered by your phoneme statistics
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

export default HowTo;